import { useEffect } from 'react';
import { POUR } from './landing-art';

/**
 * The landing page's scroll reveal.
 *
 * NOTHING HERE MAY DECIDE WHETHER THE PAGE IS READABLE. This is the login screen: a visitor who
 * cannot sign in has an outage, and a reveal is decoration. So the hiding rules in landing.css are
 * scoped to a class THIS FILE adds, and it is added only once an IntersectionObserver exists to take
 * it back off again. No observer, an exception, an old browser, JavaScript off — every one of those
 * paths leaves a page that was never hidden in the first place, rather than a blank one. The
 * sign-in band carries no reveal unit at all, for the same reason.
 *
 * TWO CLASSES, AND THE ORDER BETWEEN THEM IS THE POINT. `armed` carries the hidden STATE; `live`
 * carries the TRANSITIONS, two animation frames later. Putting both on one class means that the
 * moment it lands, every unit has a declared transition AND a brand-new target value — so the page
 * animates OUT over half a second and then back in, which is worse than no reveal at all. Found by
 * measuring: with one class, a unit's computed transform sat at its pre-arm value long after the
 * rule said otherwise, because it was still inside the transition's delay.
 */

/** Marks one unit of the reveal — the element that fades and rises as a whole. */
export const REVEAL = 'tenders-reveal';

/** Later units in a band, so a band arrives in sequence rather than all at once. */
export const REVEAL_STEP_2 = 'tenders-reveal--2';
export const REVEAL_STEP_3 = 'tenders-reveal--3';
export const REVEAL_STEP_4 = 'tenders-reveal--4';

/** How far down a band's sequence a unit sits. */
export type RevealStep = 1 | 2 | 3 | 4;

/** On <html>: the hidden state. Added only when an observer exists to undo it. */
export const REVEAL_ARMED = 'tenders-reveal-armed';

/** On <html>: the transitions. Added two frames after ARMED, never in the same one. */
export const REVEAL_LIVE = 'tenders-reveal-live';

/** On a unit that has arrived. Set once and never removed — see `unobserve` below. */
export const REVEAL_IN = 'tenders-reveal-in';

/** On a stroke that has been measured and will be drawn in. Added by `inkStrokes` below. */
export const INK = 'tenders-ink';

/** The custom property holding a stroke's own length, in user units. */
export const INK_LENGTH = '--ink-length';

/** Every stroked class in the drawings. Fills are not drawn — they arrive with the drawing itself. */
const STROKES = '.ta-s, .ta-s2, .ta-line';

/**
 * The shapes that can report a length.
 *
 * Needed because the drawings put a stroke class on a `<g>` as often as on a shape — `stroke` and
 * `fill` are inherited SVG properties, so one class on a group styles a dozen children at once. A
 * group has no length, so measuring only the classed elements silently left whole drawings out: the
 * source rail is one `<g class="ta-s">` and was appearing all at once while everything around it
 * drew.
 */
const GEOMETRY = 'path, line, polyline, polygon, circle, ellipse, rect';

/** Geometry answers `getTotalLength`; a group does not. The method IS the test. */
function measurable(el: Element): el is SVGGeometryElement {
  return typeof (el as SVGGeometryElement).getTotalLength === 'function';
}

/* A record rather than an array: `noUncheckedIndexedAccess` makes an array lookup `string |
   undefined` even when the index is a literal union, and a `!` here would be an assertion where a
   total mapping says the same thing and cannot go stale. */
const STEPS: Record<RevealStep, string> = {
  1: REVEAL,
  2: `${REVEAL} ${REVEAL_STEP_2}`,
  3: `${REVEAL} ${REVEAL_STEP_3}`,
  4: `${REVEAL} ${REVEAL_STEP_4}`,
};

/**
 * The class for the nth unit within a band, 1-based.
 *
 * Classes rather than an inline `--i` custom property because one of the units is a drawing, and
 * `landing-art.tsx` takes a `className` and nothing else. Passing the delay the same way everything
 * else is passed keeps the call sites identical rather than special-casing the one that differs.
 */
export function revealStep(step: RevealStep): string {
  return STEPS[step];
}

/**
 * Measure every stroke in the drawings so each one can be drawn in.
 *
 * MEASURED, NOT NORMALISED. `pathLength="1"` would be less code, but it is an attribute on 107 nodes
 * and it is not reliable on `<rect>` and `<circle>` at this project's browser floor — and when it is
 * not honoured, `stroke-dasharray: 1` turns a 400-unit rail into a dotted line rather than a solid
 * one. Asking each element for its own length is a feature test and a measurement at once: anything
 * that cannot answer is skipped and simply appears, which is the correct failure.
 *
 * Returns the elements it prepared, so the caller can undo exactly what it did.
 *
 * SCOPED TO DRAWINGS THAT ARE REVEAL UNITS. A drawing outside the reveal never gets a `…-in` class,
 * so inking it would hide its strokes for ever — and the drawing beside the sign-in form is exactly
 * that drawing. The selector, not a comment, is what prevents it.
 */
export function inkStrokes(): SVGGeometryElement[] {
  const inked: SVGGeometryElement[] = [];
  for (const drawing of document.querySelectorAll<SVGElement>(`.tenders-art.${REVEAL}`)) {
    /*
      A Set, because the two passes overlap: a `<path class="ta-s2">` inside a `<g class="ta-s">` is
      found once on its own and once as a descendant, and measuring it twice would write the second
      length over the first.
    */
    const strokes = new Set<SVGGeometryElement>();
    for (const marked of drawing.querySelectorAll<SVGElement>(STROKES)) {
      if (measurable(marked)) strokes.add(marked);
      else for (const child of marked.querySelectorAll<SVGGeometryElement>(GEOMETRY)) strokes.add(child);
    }

    for (const stroke of strokes) {
      // Two drawings use a dash pattern as a DESIGN choice — a dotted connector, a dotted price
      // line. Measuring them would overwrite that pattern and quietly turn both solid.
      if (stroke.hasAttribute('stroke-dasharray')) continue;
      // The beer's bubbles are already rising; drawing them at the same time is one effect too many.
      if (stroke.closest(`.${POUR}`) !== null) continue;

      let length = 0;
      try {
        length = stroke.getTotalLength();
      } catch {
        continue; // not a geometry element in this engine — leave it alone, it just appears
      }
      if (!Number.isFinite(length) || length <= 0) continue;

      /*
        The LENGTH goes inline; the OFFSET stays in the stylesheet, reading this property. That is
        what keeps the reveal rule a plain rule instead of an `!important` fighting an inline style.
      */
      stroke.style.setProperty(INK_LENGTH, `${length}`);
      stroke.style.strokeDasharray = `${length} ${length}`;
      stroke.classList.add(INK);
      inked.push(stroke);
    }
  }
  return inked;
}

/**
 * @param routeKey Re-arms the whole reveal when it changes.
 *
 * NOT COSMETIC. The landing serves more than one page from one mounted component, so a client-side
 * navigation swaps every unit in the DOM without unmounting this hook. Armed once, the new page's
 * units would never be observed — and `tenders-reveal-armed` is still on `<html>`, so they would sit
 * at `opacity: 0` for ever. A page that is blank after a nav click is the worst failure this file
 * has, and it is one dependency away.
 */
export function useReveal(routeKey: string): void {
  useEffect(() => {
    /*
      Feature-detected before anything is hidden, not after. This is the branch that keeps a browser
      without IntersectionObserver — and jsdom, which is why the component suites next door see a
      fully visible page and assert against it unchanged.
    */
    if (typeof IntersectionObserver === 'undefined') return;

    const units = document.querySelectorAll<HTMLElement>(`.${REVEAL}`);
    if (units.length === 0) return;

    /*
      Measured BEFORE arming, so the dash values land while no transition is declared. Reversed, the
      strokes would animate from whole to hidden on load — the same trap the two-class arming below
      exists to avoid, one layer down.
    */
    const inked = inkStrokes();

    const root = document.documentElement;
    root.classList.add(REVEAL_ARMED);

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add(REVEAL_IN);
          /*
            Once. A unit that re-hides when it leaves the viewport replays on every scroll up, which
            reads as a demo of a page rather than a page — and on the way back down it would hide
            content the visitor has already started reading.
          */
          io.unobserve(entry.target);
        }
      },
      // Slightly INSIDE the viewport, so a unit has committed to being on screen before it starts.
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );

    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        root.classList.add(REVEAL_LIVE);
        // Observed only now: arming and revealing inside one style recalculation would collapse the
        // two states into no visible change, and the reveal would silently never play.
        for (const unit of units) io.observe(unit);
      });
    });

    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
      io.disconnect();
      root.classList.remove(REVEAL_ARMED, REVEAL_LIVE);
      /*
        The inline dash values outlive the class that made them mean anything, and a stroke left
        with `stroke-dasharray` set and no rule pointing its offset back to zero is a solid line
        drawn as a dotted one. Cleared explicitly rather than relying on the elements unmounting.
      */
      for (const stroke of inked) {
        stroke.style.removeProperty(INK_LENGTH);
        stroke.style.removeProperty('stroke-dasharray');
        stroke.classList.remove(INK);
      }
    };
  }, [routeKey]);
}
